import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

// ============================================================
// SceneDeck——全銀幕一幕一幕導航系統（規則 59 Scene Deck 互動版）
// mode='button'：金色按鈕＋crossfade 推進（/b2b）
// mode='lever'：老虎機拉桿＋轉輪定格（/partner）
// 捲動鎖定：必須按掣先有下一步；每幕內容超高時可內部捲動
// ============================================================

export interface DeckScene {
  id: string;
  reels?: [string, string, string]; // lever 模式：轉輪定格符號
  render: () => ReactNode;
}

interface SceneDeckProps {
  scenes: DeckScene[];
  mode: 'button' | 'lever';
  labels?: { next?: string; done?: string };
  onSceneChange?: (index: number) => void;
}

const SPIN_SYMBOLS = ['🌶️', '🍜', '🎁', '🚢', '✈️', '🛒', '💰', '📦', '⚡', '🏆'];

export default function SceneDeck({ scenes, mode = 'button', labels, onSceneChange }: SceneDeckProps) {
  const [index, setIndex] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [reelFaces, setReelFaces] = useState<string[]>(['🌶️', '🍜', '🎁']);
  const [leverPulled, setLeverPulled] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const last = index >= scenes.length - 1;

  useEffect(() => {
    onSceneChange?.(index);
  }, [index, onSceneChange]);

  // 鎖定背景捲動（全銀幕體驗；卸載時還原）
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const advance = useCallback(() => {
    if (spinning) return;
    if (mode === 'lever') {
      if (leverPulled) return;
      setLeverPulled(true);
      setSpinning(true);
      const faces = ['🌶️', '🍜', '🎁'];
      setReelFaces(faces);
      const stops = [900, 1400, 1900];
      const done: boolean[] = [false, false, false];
      const iv = setInterval(() => {
        setReelFaces((prev) => prev.map((f, i) => (done[i] ? f : SPIN_SYMBOLS[Math.floor(Math.random() * SPIN_SYMBOLS.length)])));
      }, 80);
      stops.forEach((stop, i) => {
        setTimeout(() => {
          done[i] = true;
          if (done.every(Boolean)) {
            clearInterval(iv);
            setTimeout(() => {
              setSpinning(false);
              setLeverPulled(false);
              setIndex((v) => Math.min(v + 1, scenes.length - 1));
            }, 350);
          }
        }, stop);
      });
    } else {
      setIndex((v) => Math.min(v + 1, scenes.length - 1));
    }
  }, [mode, scenes.length, spinning, leverPulled]);

  const back = useCallback(() => {
    if (spinning) return;
    setIndex((v) => Math.max(v - 1, 0));
  }, [spinning]);

  const nextLabel = index >= scenes.length - 2
    ? (labels?.done || '最後一步')
    : (labels?.next || '下一頁');

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-x1493-dark overflow-hidden">
      {/* 離開出口（返主頁） */}
      <Link
        to="/"
        className="absolute left-4 top-4 z-30 text-white/50 hover:text-white text-xs font-bold tracking-widest transition"
        aria-label="離開"
      >
        ✕ 離開
      </Link>

      {/* 進度點 */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-30 flex flex-col gap-2">
        {scenes.map((s, i) => (
          <span key={s.id} className={`w-2 h-2 rounded-full transition ${i === index ? 'bg-x1493-secondary scale-125' : 'bg-white/25'}`} />
        ))}
      </div>

      {/* 場景層 */}
      {scenes.map((s, idx) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-all duration-700 ${idx === index ? 'opacity-100 translate-y-0' : 'opacity-0 pointer-events-none translate-y-6'}`}
          aria-hidden={idx !== index}
        >
          <div className="h-full overflow-y-auto overscroll-contain">{s.render()}</div>
        </div>
      ))}

      {/* 返回（第一幕不顯示） */}
      {index > 0 && !spinning && (
        <button
          onClick={back}
          className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full bg-white/10 text-white/70 flex items-center justify-center hover:bg-white/25 transition"
          aria-label="上一幕"
        >
          <ChevronLeft size={20} />
        </button>
      )}

      {/* 底部推進區：button 模式＝金色按鈕；lever 模式＝老虎機拉桿 */}
      {!spinning && (
        <div className="absolute bottom-5 left-0 right-0 z-30 flex justify-center pointer-events-none">
          {mode === 'button' && !last && (
            <button
              onClick={advance}
              className="pointer-events-auto group relative inline-flex items-center gap-3 bg-gradient-to-b from-[#e7c766] to-[#c9a13b] text-[#0b3527] font-black text-base pl-7 pr-6 py-4 rounded-full shadow-[0_10px_30px_rgba(201,161,59,.45)] hover:shadow-[0_14px_36px_rgba(201,161,59,.6)] hover:-translate-y-0.5 active:translate-y-0 transition-all"
              aria-label={nextLabel}
            >
              <span>{nextLabel}</span>
              <ChevronRight size={22} className="animate-[nudge_1s_ease-in-out_infinite]" />
            </button>
          )}
          {mode === 'lever' && !last && (
            <button
              onClick={advance}
              className="pointer-events-auto group relative flex flex-col items-center"
              aria-label="拉桿看下一頁"
            >
              <span className="mb-2 text-[11px] font-bold tracking-[0.3em] text-x1493-secondary bg-black/50 rounded-full px-3 py-1 border border-x1493-secondary/40 group-hover:border-x1493-secondary">
                PULL ▼
              </span>
              <span className="relative block w-16 h-16 rounded-full bg-gradient-to-b from-[#e7c766] to-[#b8912e] shadow-[0_0_24px_rgba(216,178,92,.5),inset_0_2px_6px_rgba(255,255,255,.5)] group-hover:shadow-[0_0_34px_rgba(216,178,92,.75)] group-active:translate-y-2 transition-transform">
                <span className="absolute inset-2 rounded-full border-2 border-[#0b3527]/40" />
              </span>
              <span className="mt-1.5 text-[10px] font-bold tracking-widest text-white/70">PULL</span>
            </button>
          )}
        </div>
      )}

      {/* 老虎機轉輪覆蓋層（lever 模式 pull 時） */}
      {spinning && mode === 'lever' && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="flex gap-3">
            {reelFaces.map((f, i) => (
              <div
                key={i}
                className={`w-20 h-24 md:w-24 md:h-28 rounded-2xl bg-white flex items-center justify-center text-5xl shadow-[0_0_30px_rgba(216,178,92,.5)] border-4 ${i === 0 ? 'border-[#e7c766]' : 'border-[#0b3527]'}`}
              >
                {f}
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`
        @keyframes nudge { 0%,100% { transform: translateX(0);} 50% { transform: translateX(6px);} }
      `}</style>
    </div>
  );
}
